import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createBotAdminClient, getBotAdminToken, setBotAdminToken } from '../../utils/botAdminApi';
import './BotAdmin.css';

const client = createBotAdminClient();

const SIDEBAR_MENU = [{ id: 'rules', label: "Do's & Don'ts", icon: '✨' }];

async function resolveDefaultPortalId() {
  try {
    const pub = await client.get('/api/config/public');
    const id = pub.data?.defaultPortalId;
    if (id && String(id).trim()) return String(id).trim();
  } catch (_) {
    /* public config optional */
  }
  const { data } = await client.get('/api/bot-admin/portals');
  const list = data.portals || [];
  return list[0]?.id || '';
}

export default function BotAdminDashboard() {
  const navigate = useNavigate();
  const [selectedPortalId, setSelectedPortalId] = useState('');
  const [dos, setDos] = useState('');
  const [donts, setDonts] = useState('');
  const [loadErr, setLoadErr] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');

  const logout = useCallback(() => {
    setBotAdminToken('');
    navigate('/bot-admin/login', { replace: true });
  }, [navigate]);

  const loadInstructions = useCallback(async (portalId, bustCache = false) => {
    if (!portalId) return;
    const { data } = await client.get(`/api/bot-admin/portals/${portalId}`, {
      params: bustCache ? { _t: Date.now() } : undefined,
    });
    setDos(data.portal?.bot_instructions_dos || '');
    setDonts(data.portal?.bot_instructions_donts || '');
    setSaveMsg('');
  }, []);

  useEffect(() => {
    if (!getBotAdminToken()) {
      navigate('/bot-admin/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadErr('');
      try {
        const portalId = await resolveDefaultPortalId();
        if (cancelled) return;
        setSelectedPortalId(portalId);
        if (!portalId) {
          setLoadErr('No portal configured. Set PORTAL_URL on the API server.');
          return;
        }
        await loadInstructions(portalId);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 401) {
            logout();
            return;
          }
          setLoadErr(e.response?.data?.error || e.message || 'Failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, loadInstructions, logout]);

  const refresh = async () => {
    setLoadErr('');
    setRefreshMsg('');
    setRefreshing(true);
    try {
      let portalId = selectedPortalId;
      try {
        const next = await resolveDefaultPortalId();
        if (next) portalId = next;
      } catch (_) {
        /* keep previous portalId */
      }
      setSelectedPortalId(portalId);
      if (!portalId) {
        setLoadErr('No portal configured. Set PORTAL_URL on the API server.');
        return;
      }
      await loadInstructions(portalId, true);
      setRefreshMsg('Loaded latest from server.');
      window.setTimeout(() => setRefreshMsg(''), 4000);
    } catch (e) {
      setLoadErr(e.response?.data?.error || e.message || 'Refresh failed');
      if (e.response?.status === 401) logout();
    } finally {
      setRefreshing(false);
    }
  };

  const saveInstructions = async (e) => {
    e.preventDefault();
    if (!selectedPortalId) return;
    setSaving(true);
    setSaveMsg('');
    try {
      await client.put(`/api/bot-admin/portals/${selectedPortalId}/instructions`, {
        bot_instructions_dos: dos,
        bot_instructions_donts: donts,
      });
      setSaveMsg('Saved. Restart running bot sessions to pick up changes.');
    } catch (ex) {
      setSaveMsg(ex.response?.data?.error || ex.message || 'Save failed');
      if (ex.response?.status === 401) logout();
    } finally {
      setSaving(false);
    }
  };

  const sidebar = (
    <>
      <div className="ba-sidebar__brand">
        <div className="ba-sidebar__logo">
          <div className="ba-sidebar__logo-mark" aria-hidden>⚙</div>
          <div>
            <div className="ba-sidebar__logo-text">Bot admin</div>
            <div className="ba-sidebar__logo-sub">Admin console</div>
          </div>
        </div>
      </div>
      <nav className="ba-sidebar__nav" aria-label="Bot admin sections">
        {SIDEBAR_MENU.map((item) => (
          <button
            key={item.id}
            type="button"
            className="ba-nav-item ba-nav-item--active"
            aria-current="page"
          >
            <span className="ba-nav-item__icon" aria-hidden>
              {item.icon}
            </span>
            {item.label}
          </button>
        ))}
      </nav>
      <div className="ba-sidebar__foot">
        <Link to="/">← Site home</Link>
        <button type="button" onClick={logout}>
          Log out
        </button>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="ba-app">
        <aside className="ba-sidebar">{sidebar}</aside>
        <div className="ba-main">
          <div className="ba-loading-app" role="status" aria-live="polite">
            <div className="ba-spinner-light" aria-hidden />
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ba-app">
      <aside className="ba-sidebar">{sidebar}</aside>

      <div className="ba-main">
        <header className="ba-main__bar">
          <h1 className="ba-main__title">Do&apos;s &amp; Don&apos;ts</h1>
          <div className="ba-main__actions">
            <button
              type="button"
              className="ba-btn-light"
              onClick={refresh}
              disabled={refreshing || !selectedPortalId}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </header>

        <div className="ba-main__body">
          {loadErr && (
            <div className="ba-flash-light ba-flash-light--err" role="alert">
              {loadErr}
            </div>
          )}
          {refreshMsg && (
            <div className="ba-flash-light ba-flash-light--ok" role="status">
              {refreshMsg}
            </div>
          )}

          <div className="ba-panel-light ba-panel-light--rules">
            <div className="ba-panel-light__head ba-panel-light__head--rules">
              <h2 className="ba-h2-light">Voice demo behavior</h2>
              <p className="ba-muted-light ba-muted-light--tight">
                One rule per line. The live bot uses these for every reply, including the opening welcome.
                Save here, then restart active demo sessions so running bots pick up changes.
              </p>
            </div>
            <form onSubmit={saveInstructions} className="ba-form-light ba-form-light--rules">
              {saveMsg && (
                <div
                  className={
                    saveMsg.startsWith('Saved')
                      ? 'ba-flash-light ba-flash-light--ok ba-flash-light--inline'
                      : 'ba-flash-light ba-flash-light--err ba-flash-light--inline'
                  }
                  role="status"
                >
                  {saveMsg}
                </div>
              )}
              <div className="ba-field-group ba-field-group--do">
                <label className="ba-label-light ba-label-light--rules">
                  <span className="ba-label-light__row">
                    <span className="ba-label-light__title">Do&apos;s</span>
                    <span className="ba-label-light__badge ba-label-light__badge--do">Encourage</span>
                  </span>
                  <span className="ba-label-light__hint">Greeting style, pacing, persona, how to summarize the UI</span>
                  <textarea
                    className="ba-textarea-light ba-textarea-light--rules"
                    rows={8}
                    value={dos}
                    onChange={(e) => setDos(e.target.value)}
                    disabled={!selectedPortalId}
                    placeholder="Start with a professional greeting: …"
                  />
                </label>
              </div>
              <div className="ba-field-group ba-field-group--dont">
                <label className="ba-label-light ba-label-light--rules">
                  <span className="ba-label-light__row">
                    <span className="ba-label-light__title">Don&apos;ts</span>
                    <span className="ba-label-light__badge ba-label-light__badge--dont">Avoid</span>
                  </span>
                  <span className="ba-label-light__hint">Actions or topics the bot must not suggest or perform</span>
                  <textarea
                    className="ba-textarea-light ba-textarea-light--rules"
                    rows={8}
                    value={donts}
                    onChange={(e) => setDonts(e.target.value)}
                    disabled={!selectedPortalId}
                    placeholder={'e.g. Click Log out\nOpen Settings'}
                  />
                </label>
              </div>
              <button
                type="submit"
                className="ba-btn-light ba-btn-light--primary ba-btn-light--rules-save"
                disabled={saving || !selectedPortalId}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
